import React from 'react';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Button,
  Toolbar,
  Typography,
  IconButton,
  Snackbar,
  DialogTitle,
  CircularProgress,
} from '@mui/material';
import { Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const PREFIX = 'FormDialogTitle';

const classes = {
  appBar: `${PREFIX}-appBar`,
  title: `${PREFIX}-title`,
  progress: `${PREFIX}-progress`,
};

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    position: 'relative',
    backgroundColor: '#fff',
  },

  [`& .${classes.title}`]: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },

  [`& .${classes.progress}`]: {
    marginRight: theme.spacing(2),
  },
}));

interface FormDialogTitleProps {
  title: string;
  snackbarOpen: boolean;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  savingError: string | null;
}

export default function FormDialogTitle({
  title,
  snackbarOpen,
  setSnackbarOpen,
  onCancel,
  onSave,
  saving,
  savingError,
}: FormDialogTitleProps) {
  const { t } = useTranslation('common');

  return (
    <StyledDialogTitle>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error">
          {savingError}
        </Alert>
      </Snackbar>
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton
            edge="start"
            color="default"
            onClick={onCancel}
            aria-label="close"
            data-testid="close"
            size="large">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          {saving && <CircularProgress className={classes.progress} />}
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={saving}>
            {t('common.save')}
          </Button>
        </Toolbar>
      </AppBar>
    </StyledDialogTitle>
  );
}
